import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Plus, Rocket } from "lucide-react";
import { PageHead, Panel } from "@/project/components/dashboard-shell";
import { formatINR } from "@/project/data/sample";
import { campaigns, partners, programs } from "@/project/data/network";

export const Route = createFileRoute("/institution/campaigns")({ component: OrgCampaigns });

function OrgCampaigns() {
  const org = partners[0];
  const mine = campaigns.filter((c) => c.orgId === org.id);
  const myPrograms = programs.filter((p) => p.orgId === org.id);
  const [open, setOpen] = useState(false);

  return (
    <>
      <PageHead
        title="Campaigns"
        subtitle={`Create, submit for approval and track campaigns for ${org.name}.`}
        action={
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 rounded-full bg-[var(--maroon)] px-4 py-2 text-sm font-semibold text-[var(--cream)]">
            <Plus className="h-4 w-4" /> New campaign
          </button>
        }
      />

      {open && (
        <Panel title="New campaign">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Campaign title" placeholder="Winter fodder for 300 cattle" />
            <Field label="Goal amount (₹)" placeholder="500000" type="number" />
            <Field label="End date" type="date" />
            <Field label="Giving domain" placeholder="Animal Seva" />
            <label className="block sm:col-span-2">
              <span className="text-xs text-foreground/60">Story for donors</span>
              <textarea rows={4} className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2 text-sm" placeholder="Why this campaign matters, and what the funds will buy." />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button className="rounded-full bg-[var(--maroon)] px-4 py-2 text-sm font-semibold text-[var(--cream)]">Submit for foundation approval</button>
            <button onClick={() => setOpen(false)} className="rounded-full border border-border px-4 py-2 text-sm font-semibold">Cancel</button>
          </div>
        </Panel>
      )}

      <div className={open ? "mt-6" : ""}>
        <Panel title={`${mine.length} campaigns`}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-xs uppercase tracking-wider text-foreground/70">
                <tr className="border-b border-border text-left"><th className="py-2">Campaign</th><th>Goal</th><th>Raised</th><th>Progress</th><th>Status</th><th>Ends</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {mine.map((c) => {
                  const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
                  return (
                    <tr key={c.id}>
                      <td className="max-w-xs truncate py-3 font-medium">{c.title}</td>
                      <td>{formatINR(c.goal)}</td>
                      <td className="font-semibold text-[var(--maroon)]">{formatINR(c.raised)}</td>
                      <td className="w-32">
                        <div className="h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
                          <div className="h-full" style={{ width: `${pct}%`, background: "var(--gradient-warm)" }} />
                        </div>
                      </td>
                      <td>{c.status}</td>
                      <td className="text-xs">{c.endDate}</td>
                      <td><Link to="/campaigns/$id" params={{ id: c.id }} className="text-xs text-[var(--maroon)] underline">View</Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <div className="mt-6">
        <Panel title="Standard monthly programmes">
          <ul className="divide-y divide-border text-sm">
            {myPrograms.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <p className="flex items-center gap-1.5 font-medium"><Rocket className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />{p.title}</p>
                  <p className="text-xs text-foreground/70">{p.impact}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="font-semibold text-[var(--maroon)]">{formatINR(p.monthly)}/mo</p>
                  <p className="text-foreground/70">{p.subscribers} subscribers</p>
                </div>
              </li>
            ))}
            {myPrograms.length === 0 && <li className="py-3 text-foreground/60">No always-on programmes yet.</li>}
          </ul>
        </Panel>
      </div>
    </>
  );
}

function Field({ label, placeholder, type = "text" }: { label: string; placeholder?: string; type?: string }) {
  return (
    <label className="block">
      <span className="text-xs text-foreground/60">{label}</span>
      <input type={type} placeholder={placeholder} className="mt-1 w-full rounded-xl border border-border bg-background px-4 py-2 text-sm" />
    </label>
  );
}